import { action, computed, observable } from 'mobx';
import {
    BillingModalType,
    IBillingFieldError,
    IBillingFormErrors,
    IBillingFormErrorsMeta,
    IInvoice,
    IPricingPlanData,
    TActivePlanCode,
    TPlanCode,
    TPricingPeriod,
    TRecurlyAddress,
    TRecurlyFields,
    IPlansAfterPriceUpdate
} from '../types/membership.types';
import {
    ICouponResponse,
    IPicTimeCouponResponse,
    TGetNextPaymentResponse
} from '~/types/server.types';
import dayjs from 'dayjs';
import { TTrackFunction } from '~/common/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import {
    CYCLE_LAST_DAY,
    CYCLE_STOP_FIRST_DAY,
    CYCLE_STOP_LAST_DAY,
    DEFAULT_PRICING_PERIOD_VALUE,
    INELIGIBLE_COUPON_CODES_FOR_SUBSCRIBERS,
    STARTER_PLAN_MAX_EARNINGS,
    URGENCY_TEST_TIME_ZONE
} from '~/components/Membership/constants/membershipData';
import {
    getCouponFullyRedeemedDate,
    getParentCouponCode,
    getPricingTier,
    isCouponFlexible,
    isDowngradePlans,
    shouldApplyPriceUpdateCoupon,
    getPricingPeriod
} from '~/components/Membership/utils/membership.utils';
import type { GlobalMXContextValue } from '~/providers/mx-global-context/global-mx.context';
import { UserStore } from '~/stores/user.store';
import { CompanyStore } from '~/stores/company.store';
import { InjectorService } from '~/services/injector.service';
import { logInfo } from '~/services/logger/logger.utils';

import { isUrgentCouponActive } from '~/utils/promo.utils';
import { UtmSources } from '~/constants/utm-sources';
import {
    TPartner,
    TPartnerComputedInfo
} from '~/apps/main/Partners/partners.types';
import { PARTNERS_CONFIG } from '~/apps/main/Partners/partners-config';
import {
    getPartnerFromUtmSource,
    isPartnerCouponEligible
} from '~/apps/main/Partners/partners.utils';
import { formatDate } from '~/utils/date.utils';
import { Coupons } from '~/constants/coupons';
import {
    setStoredCouponCode,
    getStoredCouponCode
} from '~/utils/coupons.utils';
import {
    PRICE_CHANGE_DATE,
    FLEXIBLE_COUPONS
} from '~/components/Membership/constants/flexibleCoupons';

export class MembershipUiStore {
    userStore: UserStore;
    companyStore: CompanyStore;
    injectorService: InjectorService;

    @observable selectedPlan: IPricingPlanData;
    @observable selectedPricingPeriod: TPricingPeriod =
        DEFAULT_PRICING_PERIOD_VALUE;
    @observable checkoutCouponCode: string;
    @observable isCheckoutCouponInitialised = false;
    @observable customCheckoutCouponDisplayName: string;
    @observable referralCouponCode: string;
    @observable referralCouponDisplayName: string;
    @observable referrerName: string;
    @observable referralCouponAmount: string;
    @observable isShowBillingModal: boolean;
    @observable isShowAppStoreModal: boolean;
    @observable isShowGooglePlayStoreModal: boolean;
    @observable isShowAccountSetupModal: boolean;
    @observable isShowAccountSetupPopup: boolean;
    @observable isShowSurpriseModal: boolean;
    @observable billingModalType: BillingModalType;
    @observable isBillingFormLoading: boolean;
    @observable billingFormFields: TRecurlyAddress;
    @observable billingFormErrors: IBillingFormErrors;
    @observable isSubmitButtonDisabled: boolean;
    @observable isCouponInitialized = false;
    @observable invoiceHistory: IInvoice[];
    @observable hasMoreInvoices: boolean;
    @observable isMounted: boolean;
    @observable blockedCoupons: string[];
    @observable isEligibleForCoupon: boolean;
    @observable isCouponValidForNextBillingCycle: boolean;
    @observable isDowngradedNextPlan: boolean;
    @observable isDowngradeModalOpen: boolean;
    @observable blockingChangeFeatures: string[];
    @observable nextPlanCode: TPlanCode | null;
    @observable alreadyShowedGoYearlyDialog = false;
    @observable isCouponExistInServer = true;
    @observable cachedCouponsDetails: Record<string, ICouponResponse> = {};
    @observable cachedCouponsDetailsLoadingStatuses: Record<string, boolean> =
        {};
    @observable starterPlanEarnings: number;
    @observable purchasePreviewError: any;
    @observable pictimeCouponEligibility: IPicTimeCouponResponse;
    @observable shouldShowCancellationFlow = false;
    @observable defaultCouponCode: string;
    @observable couponsWithReplacement: string[] = [];
    @observable nextPayment: IInvoice;
    @observable isUrgencyTestActive = false;
    @observable isUrgentCoupon = false;
    @observable isNewPriceChangeCouponActive = false;
    @observable isNextPaymentAfterPriceUpdate: boolean = false;
    @observable plansAfterPriceUpdate: IPlansAfterPriceUpdate[] = [];

    constructor({
        userStore,
        companyStore,
        injectorService
    }: Pick<
        GlobalMXContextValue,
        'userStore' | 'companyStore' | 'injectorService'
    >) {
        this.userStore = userStore;
        this.companyStore = companyStore;
        this.injectorService = injectorService;

        this.reset();
    }

    @computed
    get isPurchaseCheckoutOpen() {
        return this.billingModalType === BillingModalType.Create;
    }

    @computed
    get isChosePlanModalOpen() {
        return this.billingModalType === BillingModalType.ChoosePlan;
    }

    @computed
    get isChangeNextPlan() {
        return (
            this.isDowngrading ||
            (this.isDowngradedNextPlan &&
                this.subscription?.pricing_plan?.plan_type ===
                    this.selectedPlanCode)
        );
    }

    @computed
    get selectedPlanCode() {
        return this.selectedPlan?.planCode;
    }

    @computed
    get isDowngrading() {
        return isDowngradePlans(
            this.subscriptionPlanCode,
            this.selectedPlanCode
        );
    }

    @computed
    get isChangePlanCheckoutOpen() {
        return this.billingModalType === BillingModalType.UpgradePay;
    }

    @computed
    get subscription() {
        const { currUser } = this.userStore;
        return currUser.account?.subscription;
    }

    @computed
    get subscriptionPlanCode() {
        return this.subscription?.pricing_plan?.plan_type;
    }

    @computed
    get subscriptionCouponCode() {
        return this.subscription?.coupon?.code;
    }

    @computed
    get isSubscriptionCouponCodeActive() {
        return (
            !!this.subscriptionCouponCode &&
            this.subscription?.coupon?.state === 'active'
        );
    }

    @computed
    get subscriptionCouponCodeExpirationDate() {
        const subscriptionStartDate = new Date(this.subscription?.start_date);
        if (this.isSubscriptionCouponCodeActive) {
            return formatDate(
                new Date(
                    subscriptionStartDate.setFullYear(
                        subscriptionStartDate.getFullYear() + 1
                    )
                ),
                'MMMM D, YYYY'
            );
        }
    }

    @computed
    get subscriptionParentCouponCode() {
        return getParentCouponCode(this.subscriptionCouponCode);
    }

    @computed
    get isSubscriptionCouponFlexible() {
        return isCouponFlexible(this.subscriptionParentCouponCode);
    }

    @computed
    get isFlexibleCouponValidForUpgrade() {
        const planStartDate = this.subscription.pricing_plan?.plan_start_date;

        if (!planStartDate) {
            return false;
        }

        // Flexible coupons are only valid for upgrade during the first 60 days
        return dayjs().diff(dayjs(planStartDate), 'day') < 60;
    }

    @action.bound
    setIsUrgencyTestActive(value: boolean) {
        this.isUrgencyTestActive = value;
    }

    @action.bound
    setIsUrgentCoupon(value: boolean) {
        this.isUrgentCoupon = value;
    }

    @action.bound
    setIsNewPriceChangeCouponActive(value: boolean) {
        this.isNewPriceChangeCouponActive = value;
    }

    @computed
    get daysUntilUrgentCycleEnds() {
        const timeZoneDate = dayjs().tz(URGENCY_TEST_TIME_ZONE);
        const currentDay = timeZoneDate.day();

        if (currentDay == CYCLE_LAST_DAY) {
            return 0;
        }
        if (
            currentDay >= CYCLE_STOP_FIRST_DAY ||
            currentDay === CYCLE_STOP_LAST_DAY
        ) {
            return -1;
        }

        // Calculate the number of days until Thursday (4)
        return (CYCLE_LAST_DAY - currentDay + 7) % 7;
    }

    @computed
    get urgencyCycleEndDate() {
        return dayjs().weekday(CYCLE_LAST_DAY).startOf('day');
    }

    @computed
    get subscriptionApplicableCouponCode() {
        if (!this.subscription) {
            return;
        }

        // Check if subscription coupon has been fully redeemed
        const couponDetails =
            this.cachedCouponsDetails[this.subscriptionCouponCode];
        const expirationDate = getCouponFullyRedeemedDate(
            this.subscription?.pricing_plan?.plan_start_date,
            couponDetails
        );
        const isCouponFullyRedeemed = new Date() > new Date(expirationDate);

        if (isCouponFullyRedeemed) {
            return '';
        }

        if (this.isSubscriptionCouponFlexible) {
            return this.isFlexibleCouponValidForUpgrade
                ? this.subscriptionParentCouponCode
                : '';
        }
        return this.subscriptionCouponCode;
    }

    @computed
    get partnersComputedInfo(): Partial<
        Record<TPartner, TPartnerComputedInfo>
    > {
        const { currCompany } = this.companyStore;
        const {
            currUser: { hasSubscriptions, utmSource }
        } = this.userStore;

        return {
            prismm: {
                coupon: {
                    isEligible: !hasSubscriptions,
                    requiresIntegration: true
                },
                isIntegrated: currCompany.hasPrismmIntegration
            },
            pictime: {
                coupon: {
                    isEligible: this.pictimeCouponEligibility.eligible,
                    requiresIntegration: true
                },
                isIntegrated: currCompany.hasPicTimeIntegration
            },
            the_knot: {
                coupon: {
                    isEligible:
                        !hasSubscriptions &&
                        utmSource === PARTNERS_CONFIG.the_knot.utmSource,
                    requiresIntegration: true
                },
                isIntegrated: currCompany.hasTheKnotIntegration
            },
            breakthrough_invite: {
                coupon: {
                    isEligible:
                        utmSource ===
                        PARTNERS_CONFIG.breakthrough_invite.utmSource
                }
            },
            adobe: {
                coupon: {
                    isEligible: utmSource === PARTNERS_CONFIG.adobe.utmSource
                }
            },
            zoom: {
                coupon: {
                    isEligible:
                        !hasSubscriptions &&
                        utmSource === PARTNERS_CONFIG.zoom.utmSource
                }
            },
            freelancers_union: {
                coupon: {
                    isEligible:
                        utmSource ===
                        PARTNERS_CONFIG.freelancers_union.utmSource
                }
            },
            showit: {
                coupon: {
                    isEligible:
                        !hasSubscriptions &&
                        utmSource === PARTNERS_CONFIG.showit.utmSource
                }
            },
            timeline_genius: {
                coupon: {
                    isEligible:
                        !hasSubscriptions &&
                        utmSource === PARTNERS_CONFIG.timeline_genius.utmSource
                }
            },
            blaze_ai: {
                coupon: {
                    isEligible:
                        !hasSubscriptions &&
                        utmSource === PARTNERS_CONFIG.blaze_ai.utmSource
                }
            },
            rella: {
                coupon: {
                    isEligible:
                        !hasSubscriptions &&
                        utmSource === PARTNERS_CONFIG.rella.utmSource
                }
            },
            weven: {
                coupon: {
                    isEligible:
                        !hasSubscriptions &&
                        utmSource === PARTNERS_CONFIG.weven.utmSource
                }
            }
        };
    }

    @computed
    get isEligibleForNewPricingCoupon() {
        const planStartDate = this.subscription?.pricing_plan?.plan_start_date;
        const newPlanPeriod = getPricingPeriod(this.selectedPlanCode);
        const isDowngradingIntoMonthly =
            this.isDowngrading &&
            newPlanPeriod === 'monthly' &&
            this.isNewPriceChangeCouponActive;

        const price20Coupon =
            FLEXIBLE_COUPONS[Coupons.priceupdate20].codePerPlan[
                this.subscriptionPlanCode
            ];

        return (
            this.isNewPriceChangeCouponActive &&
            !isDowngradingIntoMonthly &&
            (!this.subscription?.coupon?.coupon_code ||
                this.subscription?.coupon?.coupon_code === price20Coupon) &&
            planStartDate &&
            dayjs(planStartDate).isBefore(PRICE_CHANGE_DATE)
        );
    }

    @computed
    get prefilledCouponCode() {
        const { currCompany } = this.companyStore;
        const { currUser } = this.userStore;

        const partnerCouponCode = this._getPartnerCouponCode();

        if (partnerCouponCode) {
            return partnerCouponCode;
        }

        const storedCouponCode = this.checkForReplacementCoupon(
            getStoredCouponCode()
        );

        const isStoredCouponValid = this.isStoredCouponValid(storedCouponCode);

        const shouldGetDefaultCoupon =
            !this.subscription &&
            !currCompany?.subscribed &&
            !currUser.account?.has_subscriptions;

        return (
            this.subscriptionApplicableCouponCode ||
            this.referralCouponCode ||
            (isStoredCouponValid && storedCouponCode) ||
            (shouldGetDefaultCoupon && this.defaultCouponCode) ||
            (this.isEligibleForNewPricingCoupon && Coupons.priceupdate20) ||
            ''
        );
    }

    @computed
    get isWevenPrefilledCoupon() {
        return this.prefilledCouponCode?.includes('weven');
    }

    @computed
    get isPromoMessagingVisible() {
        const { currUser } = this.userStore;
        const partnerCouponCodeByUtm =
            PARTNERS_CONFIG[getPartnerFromUtmSource(currUser.utmSource)]
                ?.couponCode;
        const partnerCouponCodeByEligibility = this._getPartnerCouponCode();

        return (
            (!!this.prefilledCouponCode && !this.referralCouponCode) ||
            !!partnerCouponCodeByUtm ||
            !!partnerCouponCodeByEligibility
        );
    }

    @computed
    get isReferralMessagingVisible() {
        return !!this.referralCouponCode;
    }

    @computed
    get passedStarterPlanLimit() {
        return this.starterPlanEarnings > STARTER_PLAN_MAX_EARNINGS;
    }

    @computed
    get isEligibleForTheKnotCoupon() {
        const { currCompany } = this.companyStore;
        const {
            currUser: { utmSource, account }
        } = this.userStore;
        return (
            utmSource === UtmSources.TheKnot &&
            currCompany.hasTheKnotIntegration &&
            !account.has_subscriptions
        );
    }

    @computed
    get isZoomCouponValid() {
        return (
            this.prefilledCouponCode.includes('zoom') &&
            !!this.cachedCouponsDetails[this.prefilledCouponCode]
        );
    }

    @computed
    get isOneBillingPeriodFreeCouponApplied() {
        return (
            this.checkoutCouponCode === Coupons.showit2025 ||
            this.prefilledCouponCode === Coupons.showit2025
        );
    }

    @action
    setInvoiceHistory(invoiceHistoryResponse: IInvoice[]) {
        this.invoiceHistory = invoiceHistoryResponse;
    }

    @action
    addMoreInvoices(invoicesToAdd: IInvoice[]) {
        this.setInvoiceHistory([...this.invoiceHistory, ...invoicesToAdd]);
        this.hasMoreInvoices = !!invoicesToAdd.length;
    }

    @action.bound
    resetInvoices() {
        this.invoiceHistory = [];
        this.hasMoreInvoices = false;
    }

    @action.bound
    setCheckoutCouponCode(couponCode: string) {
        this.checkoutCouponCode = couponCode;
    }

    @action.bound
    setIsCheckoutCouponInitialised(value: boolean) {
        this.isCheckoutCouponInitialised = value;
    }

    @action.bound
    setCustomCheckoutCouponDisplayName(couponCode: string) {
        const partnerCouponCode = this._getPartnerCouponCode();
        if (!partnerCouponCode) {
            this.customCheckoutCouponDisplayName = couponCode;
            return;
        }
    }

    @action.bound
    setPurchasePreviewError(error) {
        this.purchasePreviewError = error?.data?.error_message;

        if (this.purchasePreviewError) {
            this.setCheckoutCouponCode('');
        }
    }

    @action.bound
    setBlockingChangeFeatures(features: string[]) {
        this.blockingChangeFeatures = features;
    }

    @action
    initFeatureLose(oldPlan: TPlanCode, newPlan: TPlanCode) {
        const oldTier = getPricingTier(oldPlan);
        const newTier = getPricingTier(newPlan);

        if (oldTier === 'premium') {
            if (newTier === 'essentials') {
                // premium -> essentials
                this.setBlockingChangeFeatures([
                    'multiple_companies',
                    'priority_support',
                    'advanced_reports',
                    `team_members.${newTier}`,
                    'lead_forms'
                ]);
            } else if (newTier === 'starter') {
                // premium -> starter
                this.setBlockingChangeFeatures([
                    'automations',
                    'calendly',
                    'custom_fonts',
                    'multiple_companies',
                    'priority_support',
                    'quickbooks_integration',
                    'remove_powered_by_honeyBook',
                    'standard_advanced_reports',
                    'scheduler',
                    `team_members.${newTier}`,
                    `canva`,
                    `sms_reminders`,
                    `lead_forms`,
                    'zapier',
                    'zoom',
                    'honeybook_ai'
                ]);
            }
        } else if (oldTier === 'essentials') {
            if (newTier === 'starter') {
                // essentials -> starter
                this.setBlockingChangeFeatures([
                    'automations',
                    'calendly',
                    'custom_fonts',
                    'quickbooks_integration',
                    'team_members',
                    'remove_powered_by_honeyBook',
                    'scheduler',
                    'standard_reports',
                    'zapier',
                    'canva',
                    `sms_reminders`,
                    `lead_forms`,
                    'zoom',
                    'honeybook_ai'
                ]);
            }
        } else if (oldTier === 'legacy') {
            if (newTier === 'starter') {
                if (
                    oldPlan === 'monthly_basic' ||
                    oldPlan === 'starter_monthly'
                ) {
                    //legacy starter -> new starter
                    this.setBlockingChangeFeatures([
                        'automations',
                        'calendly',
                        'custom_fonts',
                        'expense_management',
                        'multiple_companies',
                        'profit_and_loss',
                        'quickbooks_integration',
                        'remove_powered_by_honeyBook',
                        'scheduler',
                        'zapier',
                        'zoom'
                    ]);
                } else {
                    // legacy -> starter
                    this.setBlockingChangeFeatures([
                        'automations',
                        'calendly',
                        'custom_fonts',
                        'expense_management',
                        'multiple_companies',
                        'profit_and_loss',
                        'quickbooks_integration',
                        'remove_powered_by_honeyBook',
                        'scheduler',
                        `team_members.${newTier}`,
                        'zapier',
                        'zoom'
                    ]);
                }
            }
        }
    }

    @action.bound
    setReferralCoupon({
        couponCode = '',
        couponDisplayName = '',
        referrerName = '',
        amount = ''
    } = {}) {
        this.referralCouponCode = couponCode;
        this.referralCouponDisplayName = couponDisplayName;
        this.setCustomCheckoutCouponDisplayName(couponDisplayName);
        this.referrerName = referrerName;
        this.referralCouponAmount = amount;
    }

    @action
    clearPrefilledCoupon() {
        this.setReferralCoupon();
    }

    @action.bound
    setIsCouponInitialized(isApplied: boolean) {
        this.isCouponInitialized = isApplied;
    }

    @action.bound
    resetBillingFormFields() {
        this.billingFormFields = {
            country: null,
            state: null,
            first_name: '',
            last_name: '',
            city: '',
            postal_code: '',
            address1: '',
            address2: ''
        };
    }

    @action.bound
    setIsBillingFormLoading(isLoading: boolean) {
        this.isBillingFormLoading = isLoading;
    }

    @action.bound
    setBillingFormFields(fields: Partial<TRecurlyAddress>) {
        this.billingFormFields = { ...this.billingFormFields, ...fields };
    }

    @action.bound
    showBillingModal(type: BillingModalType) {
        this.isShowBillingModal = true;
        if (type) {
            this.billingModalType = type;
        }
    }

    @action.bound
    closeBillingModal() {
        this.isShowBillingModal = false;
        this.resetBillingFormFields();
        this.setCheckoutCouponCode('');
        this.setIsCheckoutCouponInitialised(false);
    }

    @action.bound
    setIsShowAppStoreModal(isShow: boolean) {
        this.isShowAppStoreModal = isShow;
    }

    @action.bound
    setIsShowGooglePlayStoreModal(isShow: boolean) {
        this.isShowGooglePlayStoreModal = isShow;
    }

    @action.bound
    setIsShowSubscriptionStoreModal(isShow: boolean) {
        const { currUser } = this.userStore;

        if (!currUser.account.subscription?.pricing_plan) {
            return;
        }

        if (
            currUser.account.subscription.pricing_plan
                .in_app_purchase_device === 'android'
        ) {
            this.setIsShowGooglePlayStoreModal(isShow);
        } else {
            this.setIsShowAppStoreModal(isShow);
        }
    }

    @action.bound
    setIsShowAccountSetupModal(isShow: boolean) {
        this.isShowAccountSetupModal = isShow;
    }

    @action.bound
    setIsShowAccountSetupPopup(isShow: boolean) {
        this.isShowAccountSetupPopup = isShow;
    }

    @action.bound
    setIsShowSurpriseModal(isShow: boolean) {
        this.isShowSurpriseModal = isShow;
    }

    @action
    resetBillingFormErrors() {
        this.billingFormErrors = {
            fields: {
                first_name: null,
                last_name: null,
                number: null,
                cvv: null,
                month: null,
                year: null,
                postal_code: null,
                country: null,
                state: null,
                city: null,
                address1: null,
                address2: null
            },
            meta: null
        };
    }

    @action.bound
    setBillingFormErrors(errorDetails: IBillingFieldError[]) {
        errorDetails.forEach(errorItem => {
            this.billingFormErrors.fields[errorItem.field] =
                errorItem.messages?.[0];
        });
    }

    @action.bound
    clearBillingFormError(field: TRecurlyFields) {
        this.billingFormErrors.fields[field] = null;
    }

    @action
    setBillingFormMetaError(errorMessage: IBillingFormErrorsMeta) {
        this.billingFormErrors.meta = errorMessage;
    }

    @action.bound
    setIsSubmitButtonDisabled(isDisabled: boolean) {
        this.isSubmitButtonDisabled = isDisabled;
    }

    @action
    setBlockedCoupons(blockedCoupons: string[]) {
        this.blockedCoupons = blockedCoupons;
    }

    @action
    setIsEligibleForCoupon(isEligible: boolean) {
        this.isEligibleForCoupon = isEligible;
    }

    @action.bound
    setIsEligibleForPicTimeCoupon(eligibility: IPicTimeCouponResponse) {
        this.pictimeCouponEligibility = eligibility;
    }

    @action.bound
    setIsCouponValidForNextBillingCycle(couponDetails: ICouponResponse) {
        if (!couponDetails) {
            this.isCouponValidForNextBillingCycle = false;
            return;
        }

        const planStartDate = new Date(
            this.subscription?.pricing_plan?.plan_start_date
        );
        const couponDurationInMonths =
            couponDetails?.temporal_unit === 'year'
                ? 12
                : couponDetails?.temporal_amount || 0;
        const expirationDate = new Date(
            planStartDate.setMonth(
                planStartDate.getMonth() + couponDurationInMonths
            )
        );

        const nextPaymentDate =
            this.subscription?.pricing_plan?.next_charge_date;

        this.isCouponValidForNextBillingCycle = dayjs(
            expirationDate.toString()
        ).isAfter(dayjs(nextPaymentDate), 'hour');

        if (!this.isCouponValidForNextBillingCycle && this.isChangeNextPlan) {
            this.setCheckoutCouponCode('');
        }
    }

    @action.bound
    setIsMounted(isMounted: boolean) {
        this.isMounted = isMounted;
    }

    @action.bound
    setIsDowngradedNextPlan(
        isDowngraded: boolean,
        nextPlanCode: TPlanCode | null
    ) {
        this.isDowngradedNextPlan = isDowngraded;
        this.setNextPlanCode(nextPlanCode);
    }

    @action.bound
    setNextPlanCode(nextPlanCode: TPlanCode | null) {
        this.nextPlanCode = nextPlanCode;
    }

    @action.bound
    reset() {
        this.selectedPlan = null;
        this.checkoutCouponCode = null;
        this.isShowBillingModal = false;
        this.isShowAppStoreModal = false;
        this.isShowGooglePlayStoreModal = false;
        this.isShowAccountSetupModal = false;
        this.billingModalType = null;
        this.isSubmitButtonDisabled = false;
        this.invoiceHistory = [];
        this.hasMoreInvoices = true;
        this.setIsEligibleForPicTimeCoupon({ eligible: false, reason: null });
        this.setIsBillingFormLoading(false);
        this.setIsMounted(false);
        this.resetBillingFormFields();
        this.resetBillingFormErrors();
    }

    @action.bound
    setIsDowngradeModalOpen(isShow: boolean) {
        this.isDowngradeModalOpen = isShow;
    }

    @action.bound
    setCouponsDetails(couponCode: string, couponDetails: ICouponResponse) {
        this.cachedCouponsDetails[couponCode] = couponDetails;

        // For flexible coupons, it is convenient to save the details for the parent coupon code
        // knowing that the coupon stored in the localstorage is the parent coupon code.
        const parentCouponCode = getParentCouponCode(couponCode);
        this.cachedCouponsDetails[parentCouponCode] = couponDetails;
    }

    @action.bound
    setCachedCouponsDetailsLoadingStatuses(
        couponCode: string,
        isLoading: boolean
    ) {
        this.cachedCouponsDetailsLoadingStatuses[couponCode] = isLoading;
    }

    @action.bound
    setSelectedPricingPeriod(period: TPricingPeriod) {
        this.selectedPricingPeriod = period;
    }

    @action.bound
    setAlreadyShowedGoYearlyDialog(value: boolean) {
        this.alreadyShowedGoYearlyDialog = value;
    }

    @action.bound
    setShouldShowCancellationFlow(value: boolean) {
        this.shouldShowCancellationFlow = value;
    }

    @action.bound
    closeCancellationFlow() {
        this.setShouldShowCancellationFlow(false);
    }

    @action.bound
    setIsCouponExistInServer(value: boolean) {
        this.isCouponExistInServer = value;
    }

    @action.bound
    setNextPayment(value: IInvoice) {
        this.nextPayment = value;
    }

    @action.bound
    checkIfNextPaymentIsDowngraded(invoice: TGetNextPaymentResponse) {
        const planCode =
            invoice[0]?.attributes?.line_items[0].attributes?.plan_code;
        this.setIsDowngradedNextPlan(
            isDowngradePlans(
                this.subscription?.pricing_plan?.plan_type,
                planCode
            ),
            planCode
        );
    }

    get isUpgrade() {
        return !!this.selectedPlanCode && !this.isDowngrading;
    }

    @action.bound
    setSelectedPlan(selectedPlan: IPricingPlanData) {
        this.selectedPlan = selectedPlan;
    }

    @action.bound
    handleChoosePlan(track: TTrackFunction) {
        const { currUser } = this.userStore;

        if (this.isDowngrading) {
            this.initFeatureLose(
                this.subscriptionPlanCode,
                this.selectedPlanCode
            );
        }

        if (this.blockingChangeFeatures?.length > 0) {
            this.setIsDowngradeModalOpen(true);
            track(AnalyticsEvents.PricingPlanSelected, {
                action: 'loaded',
                from_plan:
                    currUser.account.subscription?.pricing_plan?.plan_type,
                plan_type: this.selectedPlan.pricingTier,
                is_change:
                    this.isChosePlanModalOpen && currUser.account?.subscription
            });
            return;
        }

        this.showBillingModal(
            this.isChosePlanModalOpen && currUser.account?.subscription
                ? BillingModalType.UpgradePay
                : BillingModalType.Create
        );
    }

    @action.bound
    setStarterPlanEarnings(earning: number) {
        this.starterPlanEarnings = earning;
    }

    @action.bound
    isCouponExpired(couponCode: string) {
        if (this.isUrgentCoupon && !isUrgentCouponActive()) {
            return true;
        }
        return this.cachedCouponsDetails[couponCode]?.state === 'expired';
    }

    @action.bound
    isStoredCouponValid(couponCode: string) {
        const { currUser } = this.userStore;

        const isStoredCouponExpired =
            !!this.cachedCouponsDetails[couponCode] &&
            this.isCouponExpired(couponCode);

        const isStoredCouponIneligible =
            INELIGIBLE_COUPON_CODES_FOR_SUBSCRIBERS.includes(
                couponCode as Coupons
            ) && currUser.isSubscribed;

        return !isStoredCouponExpired && !isStoredCouponIneligible;
    }

    @action.bound
    isBlockedCoupon(couponCode: string) {
        return this.blockedCoupons.includes(couponCode?.toLowerCase());
    }

    @action.bound
    isCouponApplicableToPlan(couponCode: string, planCode: TActivePlanCode) {
        if (!couponCode) {
            return false;
        }

        if (this.isCouponExpired(couponCode)) {
            return false;
        }

        // No special restrictions for trialers
        if (!this.subscription) {
            return true;
        }

        // We want to fetch and display the current subscription discount
        if (planCode === this.subscriptionPlanCode) {
            return true;
        }

        const parentCouponCode = getParentCouponCode(couponCode);
        const isFlexibleCoupon = isCouponFlexible(parentCouponCode);
        const isNewPricePlanCouponEligible = shouldApplyPriceUpdateCoupon(
            planCode,
            this.subscription,
            this.isNewPriceChangeCouponActive
        );

        if (isNewPricePlanCouponEligible) {
            return true;
        }

        // Downgrade
        if (isDowngradePlans(this.subscriptionPlanCode, planCode)) {
            return isFlexibleCoupon
                ? false
                : this.isCouponValidForNextBillingCycle;
        }

        // Upgrade
        return isFlexibleCoupon ? this.isFlexibleCouponValidForUpgrade : true;
    }

    @action.bound
    setDefaultCouponCode(couponCode: string) {
        this.defaultCouponCode = couponCode;
    }

    @action.bound
    setCouponsWithReplacement(couponCodes: string[]) {
        this.couponsWithReplacement = couponCodes;
    }

    @action.bound
    setIsNextPaymentAfterPriceUpdate(value: boolean) {
        this.isNextPaymentAfterPriceUpdate = value;
    }

    @action.bound
    setPlansAfterPriceUpdate(plans: IPlansAfterPriceUpdate[]) {
        this.plansAfterPriceUpdate = plans;
    }

    checkForReplacementCoupon(couponCode: string) {
        const couponAfterReplacement = this.couponsWithReplacement.includes(
            couponCode
        )
            ? this.defaultCouponCode
            : couponCode;

        if (couponCode !== couponAfterReplacement) {
            setStoredCouponCode(couponAfterReplacement);

            const { currUser } = this.userStore;
            logInfo(
                '[PROMO] Coupon code was replaced from the coupon service.',
                {
                    oldCouponCode: couponCode,
                    couponAfterReplacement,
                    userId: currUser?._id,
                    statsigStableId: currUser?.statsig_stable_id
                }
            );
        }

        return couponAfterReplacement;
    }

    onLogout() {
        this.reset();
        this.setNextPayment(null);
        this.setIsCouponInitialized(false);
        this.setDefaultCouponCode(null);
        this.isEligibleForCoupon = true;
        this.clearPrefilledCoupon();
    }

    _getPartnerCouponCode() {
        const {
            currUser: { utmCustomer }
        } = this.userStore;

        const [eligiblePartner] =
            Object.entries(this.partnersComputedInfo).find(
                ([, partnerComputedInfo]) => {
                    return isPartnerCouponEligible(partnerComputedInfo);
                }
            ) || [];

        const eligiblePartnerCouponCode = eligiblePartner
            ? PARTNERS_CONFIG[eligiblePartner].couponCode
            : null;

        return utmCustomer
            ? `${eligiblePartnerCouponCode}-${utmCustomer}`
            : eligiblePartnerCouponCode;
    }
}
